// React & Hooks
import React, { useCallback, useMemo, useState } from "react";

// Helpers
import { formatCurrency, transformDateToLocalDateString } from "helpers";

// API & Tipovi
import { FleetVehicleRow } from "services/api/fleet/vehicles/types";

// Tipovi specifični za komponentu
import { VehicleDropdownProps } from "./types";
import { DropdownOption } from "../Dropdown/types";
import { usePaginatedVehicles, usePreselectedVehicle } from "./hooks";

// UI Komponente
import { DropdownAutocomplete } from "../DropdownAutocomplete";
import { Button } from "ui/components/Button/Button";
import VehicleEditCard from "ui/modules/fleet/vehicles/pages/VehicleDetailPage/components/VehicleDetailCard/components/VehicleEditCard/VehicleEditCard";


// Redak tooltipa vozila (isti izgled kao VehicleCell tooltip).
const VehicleTooltipRow: React.FC<{ label: string; value: React.ReactNode; strong?: boolean }> = ({ label, value, strong }) => (
  <div className="flex justify-between text-sm mb-1">
    <span className="text-slate-300">{label}</span>
    <span className={strong ? "font-bold text-green-400" : "font-medium text-right max-w-[180px] truncate"}>{value}</span>
  </div>
);

// Bogati tooltip vozila — prikaz na hover ikone u dropdownu (kao VehicleCell).
const VehicleIconTooltip: React.FC<{ vehicle: FleetVehicleRow }> = ({ vehicle }) => {
  const fullName =
    [vehicle.vehicleManufacturer?.name, vehicle.vehicleModel?.name, vehicle.vehicleType?.name]
      .filter(Boolean)
      .join(" ") || vehicle.name || "";
  const kw = vehicle.vehicleType?.maximumPowerKw;
  const hp = vehicle.vehicleType?.maximumPowerHp;
  const fuel = vehicle.vehicleType?.fuelType?.name;

  return (
    <div className="min-w-[280px]">
      {vehicle.picture && (
        <div className="mb-3 rounded overflow-hidden">
          <img src={vehicle.picture} alt={fullName} className="w-full h-[120px] object-cover rounded" />
        </div>
      )}
      <div className="text-xs uppercase text-slate-400 mb-2 font-semibold">Detalji vozila</div>
      <VehicleTooltipRow label="Naziv" value={fullName} />
      {vehicle.vinNumber && <VehicleTooltipRow label="VIN" value={<span className="text-xs">{vehicle.vinNumber}</span>} />}
      {vehicle.registrationPlate && <VehicleTooltipRow label="Registracija" value={vehicle.registrationPlate} />}
      {vehicle.firstRegistrationDate && (
        <VehicleTooltipRow label="Prva registracija" value={transformDateToLocalDateString(vehicle.firstRegistrationDate)} />
      )}
      {vehicle.colorName && <VehicleTooltipRow label="Boja" value={vehicle.colorName} />}
      {fuel && <VehicleTooltipRow label="Gorivo" value={fuel} />}
      {kw != null && kw > 0 && <VehicleTooltipRow label="Snaga" value={`${kw} kW${hp != null ? ` / ${hp} KS` : ""}`} />}
      {vehicle.kmTraveled != null && vehicle.kmTraveled > 0 && (
        <VehicleTooltipRow label="Kilometraža" value={`${vehicle.kmTraveled.toLocaleString()} km`} />
      )}
      {((vehicle.price?.sellingPrice ?? 0) > 0 || (vehicle.price?.netPrice ?? 0) > 0) && (
        <>
          <div className="border-t border-slate-600 my-2" />
          {(vehicle.price?.netPrice ?? 0) > 0 && (
            <VehicleTooltipRow label="Neto cijena" value={formatCurrency.format(vehicle.price!.netPrice!)} />
          )}
          {(vehicle.price?.sellingPrice ?? 0) > 0 && (
            <VehicleTooltipRow label="Prodajna cijena" value={formatCurrency.format(vehicle.price!.sellingPrice!)} strong />
          )}
        </>
      )}
    </div>
  );
};

const VehicleDropdown: React.FC<VehicleDropdownProps> = (
  props: VehicleDropdownProps
) => {
  const {
    name,
    disabled,
    required,
    label,
    error,
    value,
    direction,
    width,
    height,
    placeholder,
    onChange,
    showAddNew = true,
    cssClassName,
  } = props;

  const [showAddNewModal, setShowAddNewModal] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>("");

  const { vehicles, isLoading, hasMore, loadMore, refresh } = usePaginatedVehicles(searchValue);
  const preselectedVehicle = usePreselectedVehicle(value, vehicles);

  const handleSearchClick = useCallback(() => {
    setSearchValue("");
  }, []);

  const handleSearchChange = useCallback((name: string) => {
    setSearchValue(name);
  }, []);

  const handleCreateFleetVehicle = useCallback(async (e: any, name: string) => {
    e.preventDefault();
    setShowAddNewModal(true);
  }, []);

  const handleVehicleCreated = useCallback((newVehicleId: string) => {
    // Odaberi kreirano vozilo (postavi value preko onChange) + osvježi listu.
    onChange({ target: { name, value: newVehicleId } } as any, name, { value: newVehicleId, text: "" });
    refresh();
    setShowAddNewModal(false);
  }, [name, onChange, refresh]);

  const toOption = (i: FleetVehicleRow): DropdownOption => ({
    value: i.id,
    text: (
      <div className="flex items-center justify-between w-full">
        <span>{i.name} </span>
        {i.registrationPlate && (
          <span className="text-xs bg-blue-200 text-blue-900 py-0.5 px-2 rounded-sm block ml-2.5">{i.registrationPlate}</span>
        )}
      </div>
    ),
    image: `${i.picture ?? ""}`,
    tooltip: i.picture ? <VehicleIconTooltip vehicle={i} /> : undefined,
  });

  const vehicleItems: DropdownOption[] = useMemo(() => {
    const options = vehicles.map(toOption);
    // Preselektirano vozilo s neke kasnije stranice — ubaci ga da se prikaže u inputu.
    if (preselectedVehicle && !vehicles.some((v) => v.id === preselectedVehicle.id)) {
      options.unshift(toOption(preselectedVehicle));
    }
    return options;
  }, [vehicles, preselectedVehicle]);

  // Odabrano vozilo — za bogati tooltip na hover ikone.
  const selectedVehicle = useMemo(
    () => vehicles.find((v) => v.id === value) ?? preselectedVehicle ?? undefined,
    [vehicles, value, preselectedVehicle]
  );

  return (
    <>
      <div className="flex items-end ">
        <div className="flex-1">
          <DropdownAutocomplete
            label={label}
            name={name}
            disabled={disabled}
            required={required}
            placeholder={placeholder}
            value={value}
            direction={direction}
            width={width}
            height={height}
            options={vehicleItems}
            onChange={onChange}
            onSearchChange={handleSearchChange}
            onSearchFocus={handleSearchClick}
            error={error}
            onCreate={handleCreateFleetVehicle}
            isLoading={isLoading}
            onLoadMore={hasMore ? loadMore : undefined}
            tooltip={selectedVehicle ? <VehicleIconTooltip vehicle={selectedVehicle} /> : undefined}
            inputClassName={[!disabled && showAddNew ? "!rounded-r-none" : "", cssClassName].filter(Boolean).join(" ") || undefined}
          />
        </div>
        {!disabled && showAddNew && (
          <Button
            variant="primary"
            startIcon="/media/icons/plus.svg"
            onClick={(e) => handleCreateFleetVehicle(e, "")}
            disabled={disabled}
          />
        )}
      </div>
      {showAddNewModal && (
        <VehicleEditCard
          vehicleId={null}
          show={showAddNewModal}
          onSuccess={handleVehicleCreated}
          onClose={() => setShowAddNewModal(false)}
        />
      )}
    </>
  );
};

export default React.memo(VehicleDropdown);
